// brain.ts — the client side of the chat brain (the ONE billed server call per message).
// LIGHT shell: the app sends only its in-progress drafts as context (so the brain can reference
// "the second one"). The customer's CONNECTED accounts + review queue are read SERVER-side.
// The brain returns { reply, actions: [...] } — a QUEUE of actions the app executes in order
// (G17: "draft one for Mastodon and one for Bluesky" must do BOTH). A legacy singular `action`
// is still accepted from older server responses. Auth = the user's Oriora Supabase session.

import { getAccessToken } from '../supabase';
import { maybeWebContext } from '../search';
import { ChatAction } from './actions';
import { listDrafts, loadPersonality } from '../store/drafts';

const API = (import.meta as any).env?.VITE_API_URL || 'https://api.orioralabs.com';
const BRAIN_URL = `${API}/api/apps/oriora-social/chat`;

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}
export interface BrainResponse {
  reply: string;
  actions: ChatAction[];
}

export async function askBrain(history: ChatTurn[]): Promise<BrainResponse> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in to Oriora.');

  const drafts = listDrafts().slice(0, 20).map((d) => ({
    id: d.id,
    status: d.status,
    platform: d.platform,
    preview: d.content.slice(0, 80),
  }));

  // §5e — the AI directs, the DEVICE searches. maybeWebContext decides whether the newest user turn
  // needs current information; if so the search AND the page read happen on this device against
  // DuckDuckGo's keyless endpoint (Oriora never holds a search key) and the findings ride along as
  // extra context. Unlike the single-`message` apps, this brain takes a messages ARRAY, so the
  // context has to attach to the LAST USER turn — appending it to the array as a new entry would
  // read as the user having said it, and prepending to history would attach it to the wrong turn.
  const augmented = [...history];
  for (let i = augmented.length - 1; i >= 0; i--) {
    if (augmented[i]?.role === 'user' && typeof augmented[i]?.content === 'string') {
      const ctx = await maybeWebContext(augmented[i].content);
      if (ctx) augmented[i] = { ...augmented[i], content: augmented[i].content + ctx };
      break;
    }
  }

  const r = await fetch(BRAIN_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: augmented, context: { drafts, personality: loadPersonality() } }),
  });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new Error(`Brain HTTP ${r.status}: ${body.slice(0, 200)}`);
  }
  const j: any = await r.json().catch(() => ({}));
  // G17: prefer the actions ARRAY; fall back to the legacy singular field.
  const actions: ChatAction[] = Array.isArray(j.actions) && j.actions.length
    ? (j.actions as ChatAction[])
    : [((j.action as ChatAction) ?? { kind: 'none' })];
  return { reply: j.reply ?? '', actions };
}
